import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';

function loginRedirect(router: Router, state: RouterStateSnapshot) {
  // Preserve where the user was trying to go so we can send them back after
  // re-authenticating. Skip when bouncing from /login itself to avoid loops.
  const returnUrl = state.url && state.url !== '/login' ? state.url : undefined;
  router.navigate(['/login'], returnUrl ? { queryParams: { returnUrl } } : undefined);
}

export const authGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  if (!auth.isLoggedIn()) {
    loginRedirect(router, state);
    return false;
  }
  // This guard wraps the whole shell, so it's the one place that keeps a
  // station display session out of the app entirely — nine shell routes carry
  // no guard of their own and would otherwise be reachable by typing a URL on
  // the station PC. The API allowlists the same session down to one endpoint
  // (restrictDisplaySession, src/index.tsx), so this is the cosmetic half of
  // a gate that's enforced on the server too.
  if (auth.effectiveUser?.is_display) {
    router.navigate(['/display']);
    return false;
  }
  return true;
};

// Only a station display account — or a real admin, so the board can be
// checked from a desk without logging into the kiosk. Reads `effectiveUser`
// like every other guard here, which also means an admin who has switched
// Role Preview to firefighter is bounced, same as a firefighter would be.
export const displayGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const user = auth.effectiveUser;
  if (user && (!!user.is_display || user.role === 'admin')) return true;
  if (user) { router.navigate(['/active-calls']); return false; }
  loginRedirect(router, state);
  return false;
};

// dispatcherGuard/officerGuard/adminGuard read `effectiveUser`, not
// `currentUser` — while a real admin has an active Role Preview, these
// guards see the previewed role, so route access matches what that role
// would actually experience (e.g. previewing as firefighter and navigating
// straight to /admin-settings redirects away, same as a real firefighter).
// `auth.currentUser` (the real, un-previewed session) still drives
// `isLoggedIn()` below — logged-in-ness is never something Role Preview
// should be able to fake.
export const dispatcherGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const user = auth.effectiveUser;
  if (user && ['admin', 'dispatcher'].includes(user.role)) return true;
  if (user) { router.navigate(['/active-calls']); return false; }
  loginRedirect(router, state);
  return false;
};

export const officerGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const user = auth.effectiveUser;
  if (user && (user.role === 'admin' || !!user.is_officer)) return true;
  if (user) { router.navigate(['/active-calls']); return false; }
  loginRedirect(router, state);
  return false;
};

// Union of dispatcherGuard and officerGuard — admin, dispatcher, or
// is_officer. Used for pages that should stay view-only for officers
// (Dispatch Board, Directory) without opening them to every firefighter, and
// without loosening dispatcherGuard/officerGuard themselves (activity-log
// stays dispatcher-only; officer-review stays admin/is_officer-only).
export const dispatchViewGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const user = auth.effectiveUser;
  if (user && (['admin', 'dispatcher'].includes(user.role) || !!user.is_officer)) return true;
  if (user) { router.navigate(['/active-calls']); return false; }
  loginRedirect(router, state);
  return false;
};

export const adminGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const user = auth.effectiveUser;
  if (user && user.role === 'admin') return true;
  if (user) { router.navigate(['/active-calls']); return false; }
  loginRedirect(router, state);
  return false;
};
